<template>
  <div class="tool-guide-wrap">
    <section class="tool-guide">
      <div class="tool-guide-inner">
        <article v-for="section in sections" :key="section.title" class="tool-guide-block">
          <h2>{{ section.title }}</h2>
          <p v-for="text in section.paragraphs || []" :key="text">{{ text }}</p>
          <ul v-if="section.bullets?.length">
            <li v-for="bullet in section.bullets" :key="bullet">{{ bullet }}</li>
          </ul>
          <p v-if="section.links?.length" class="tool-guide-links">
            <template v-for="(link, index) in section.links" :key="link.path">
              <NuxtLink :to="localePath(link.path)">{{ link.label }}</NuxtLink><span v-if="index < section.links.length - 1" aria-hidden="true"> · </span>
            </template>
          </p>
        </article>
      </div>
    </section>

    <section class="tool-faq">
      <div class="tool-faq-inner">
        <h2>{{ faqTitle }}</h2>
        <div class="tool-faq-list">
          <details v-for="item in faq" :key="item.q" class="tool-faq-item">
            <summary>{{ item.q }}</summary>
            <p>{{ item.a }}</p>
          </details>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
// Written guidance under a tool page: titled sections plus an FAQ that also emits FAQPage JSON-LD.
// The .tool-faq styles come from ToolShell's default-slot extras.
const props = defineProps({
  sections: { type: Array, required: true },
  faqTitle: { type: String, required: true },
  faq: { type: Array, required: true },
});

const { localePath } = useWorkspaceLocale();

useHead(() => ({
  script: [
    {
      key: "tool-faq-schema",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: props.faq.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      }),
    },
  ],
}));
</script>

<style scoped>
.tool-guide {
  border-top: 1px solid #e8eaee;
  background: #fff;
  padding: 3.75rem 0 1rem;
}

.tool-guide-inner {
  display: grid;
  gap: 2.5rem;
  max-width: 66rem;
  margin-inline: auto;
  padding-inline: 1.25rem;
}

@media (min-width: 640px) { .tool-guide-inner { padding-inline: 1.75rem; } }

.tool-guide-block { max-width: 48rem; }

.tool-guide-block h2 {
  font-size: clamp(1.35rem, 2.5vw, 1.8rem);
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.tool-guide-block p {
  margin-top: 0.8rem;
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1.75;
}

.tool-guide-block ul {
  margin-top: 0.8rem;
  padding-left: 1.2rem;
  list-style: disc;
  color: #4b5563;
  font-size: 0.95rem;
  line-height: 1.75;
}

.tool-guide-block li + li { margin-top: 0.35rem; }

.tool-guide-links a {
  color: #0e7490;
  font-weight: 750;
}
</style>
